import React, { useMemo } from "react";
import { useTheme, Alert } from "@mui/material";
import Scrollable from "v2/components/base/Scrollable/ui";
import { GroupedShillData } from "../types";
import CabalGroupBlock from "./CabalGroupBlock";

interface ShillScrollableListProps {
  groupedData: GroupedShillData;
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

/**
 * Scrollable container for displaying grouped shill data
 */
const ShillScrollableList: React.FC<ShillScrollableListProps> = ({
  groupedData,
  isLoading,
  isError,
  error,
}) => {
  const theme = useTheme();

  // Memoize the group names to prevent unnecessary re-renders
  const groupNames = useMemo(() => {
    return Object.keys(groupedData).sort((a, b) => {
      // Sort by size if available (largest first)
      const sizeA = typeof groupedData[a].size === "number" ? groupedData[a].size : 0;
      const sizeB = typeof groupedData[b].size === "number" ? groupedData[b].size : 0;
      return (sizeB as number) - (sizeA as number);
    });
  }, [groupedData]);

  // Error state
  if (isError) {
    return <Alert severity="error">{error?.message || "Failed to load data."}</Alert>;
  }

  // Render the scrollable list of cabal groups
  return (
    <Scrollable
      sx={{
        paddingTop: "8px",
        height: "calc(100vh - 180px)", // Adjust as needed for header/cards
        minHeight: 200,
        gap: "24px",
        overflowY: "auto",
      }}
      isEmpty={!isLoading && groupNames.length === 0}
      isLoading={isLoading}
      hasMore={false}
      onLoadMore={() => {}}
      hasAllFetched={true}
      fullWidth>
      {groupNames.map((groupName) => (
        <CabalGroupBlock key={groupName} groupData={groupedData[groupName]} />
      ))}
    </Scrollable>
  );
};

export default React.memo(ShillScrollableList);
